/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Descriptions, Select, Form, Divider, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const ZonalRestriction = ({ form, axisData, restrictions }) => {
  const { t } = useTranslation();

  const { restriction, value1, value2 } = restrictions;

  const [axis, setAxis] = useState([]);
  const [selectedAx, setSelectedAx] = useState(null);
  const [uats, setUats] = useState([]);

  const ZERO_TRANSITIONS_NO = 0;

  useEffect(() => {
    if (!axisData) return;

    if (value1 !== undefined && value1 === 0 && value2 === 0) {
      const adminUnits = Array.from(
        new Map(
          axisData
            .flatMap((axisList) => axisList.administrativeUnits ?? [])
            .map((unit) => [unit.id, unit]),
        ).values(),
      );
      setUats(adminUnits);
    } else {
      if (value1 !== undefined && value1 === 0 && value2 > 0) {
        const axisWithAtLeastPassTransitions = axisData.filter(
          (aau) => aau.administrativeUnits.length >= value2 + 1,
        );
        setAxis(axisWithAtLeastPassTransitions);
      } else {
        setAxis(axisData);
      }
      const savedAxisId = form.getFieldValue(['product', 'zonal', 'axisId']);
      if (savedAxisId) {
        setSelectedAx(savedAxisId);
      }
    }
  }, [axisData, form, value1, value2]);

  const selectedAxis = axis.find((ax) => ax.id === selectedAx);

  return (
    <>
      <Divider dashed />
      <Typography.Paragraph>
        {t(`entity.offers.restrictions.restriction.${restriction}._`)}
      </Typography.Paragraph>
      <Descriptions bordered column={1}>
        {value1 !== undefined &&
          // eslint-disable-next-line no-nested-ternary
          (value1 === 0 ? (
            value2 === 0 ? (
              <Descriptions.Item
                label={t(
                  'entity.offers.restrictions.restriction.ZONAL.zeroTransitionsUAT',
                )}
              >
                <Form.Item
                  name={['product', 'zonal', 'option1']}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'entity.offers.restrictions.restriction.ZONAL.axisRequired',
                      ),
                    },
                  ]}
                >
                  <Select
                    placeholder={t(
                      'entity.offers.restrictions.restriction.ZONAL.zeroTransitionsUAT',
                    )}
                    style={{ width: '100%' }}
                    allowClear
                    onChange={(value) => {
                      form.setFieldsValue({
                        product: {
                          zonal: {
                            option1: value,
                            option2: value,
                          },
                        },
                      });
                    }}
                  >
                    {uats.map((unit) => (
                      <Select.Option key={unit.id} value={unit.id}>
                        {unit.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name={['product', 'zonal', 'option2']} hidden />
              </Descriptions.Item>
            ) : (
              <>
                {/* Axis select */}
                <Descriptions.Item>
                  <Form.Item
                    name={['product', 'zonal', 'axisId']}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'entity.offers.restrictions.restriction.ZONAL.axisRequired',
                        ),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t(
                        'entity.offers.restrictions.restriction.ZONAL.selectAxis',
                      )}
                      onChange={(value) => {
                        setSelectedAx(value);

                        form.setFieldsValue({
                          product: {
                            zonal: {
                              option1: undefined,
                              option2: undefined,
                            },
                          },
                        });
                      }}
                      style={{ width: '100%' }}
                    >
                      {axis.map((ax) => (
                        <Select.Option value={ax.id} key={ax.id}>
                          {ax.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Descriptions.Item>

                {selectedAxis && (
                  <Form.Item hidden name={['product', 'zonal', 'productId']} />
                )}

                {/* Option1 select */}
                {selectedAxis && (
                  <Descriptions.Item
                    label={t(
                      'entity.offers.restrictions.restriction.ZONAL.uatStart',
                    )}
                  >
                    <Form.Item shouldUpdate>
                      {() => {
                        const option2Value = form.getFieldValue([
                          'product',
                          'zonal',
                          'option2',
                        ]);

                        // Compute allowed units for option1 based on value2
                        let allowedOption1 = [];
                        const units = selectedAxis.administrativeUnits || [];

                        if (value2 === 0 && option2Value) {
                          allowedOption1 = units.filter(
                            (u) => String(u.id) === String(option2Value),
                          );
                        } else if (value2 === 1 && option2Value) {
                          const index = units.findIndex(
                            (u) => String(u.id) === String(option2Value),
                          );
                          if (index > 0) allowedOption1.push(units[index - 1]);
                          if (index < units.length - 1)
                            allowedOption1.push(units[index + 1]);
                        } else if (value2 === 2) {
                          if (!option2Value) {
                            allowedOption1 = units.length
                              ? [units[0], units[units.length - 1]]
                              : [];
                          } else {
                            const index = units.findIndex(
                              (u) => String(u.id) === String(option2Value),
                            );
                            if (index === 0 && units.length) {
                              allowedOption1 = [units[units.length - 1]];
                            } else if (
                              index === units.length - 1 &&
                              units.length
                            ) {
                              allowedOption1 = [units[0]];
                            } else if (units.length) {
                              // If a middle element was selected, still restrict to first and last
                              allowedOption1 = [
                                units[0],
                                units[units.length - 1],
                              ];
                            } else {
                              allowedOption1 = [];
                            }
                          }
                        } else {
                          allowedOption1 = units;
                        }

                        return (
                          <Form.Item
                            name={['product', 'zonal', 'option1']}
                            rules={[
                              {
                                required: true,
                                message: t(
                                  'entity.offers.restrictions.restriction.ZONAL.axisRequired',
                                ),
                              },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (
                                    !value ||
                                    String(value) !== String(option2Value) ||
                                    value2 === ZERO_TRANSITIONS_NO
                                  ) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    new Error(
                                      t(
                                        'entity.offers.restrictions.restriction.ZONAL.sameUatNotAllowed',
                                      ),
                                    ),
                                  );
                                },
                              }),
                            ]}
                          >
                            <Select
                              placeholder={t(
                                'entity.offers.restrictions.restriction.ZONAL.uatStart',
                              )}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              {allowedOption1.map((unit) => (
                                <Select.Option key={unit.id} value={unit.id}>
                                  {unit.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Descriptions.Item>
                )}

                {/* Option2 select */}
                {selectedAxis && (
                  <Descriptions.Item
                    label={t(
                      'entity.offers.restrictions.restriction.ZONAL.uatStop',
                    )}
                  >
                    <Form.Item shouldUpdate>
                      {() => {
                        const option1Value = form.getFieldValue([
                          'product',
                          'zonal',
                          'option1',
                        ]);

                        // Compute allowed units for option2 based on value2
                        let allowedOption2 = [];
                        const units = selectedAxis.administrativeUnits || [];

                        if (value2 === 0 && option1Value) {
                          allowedOption2 = units.filter(
                            (u) => String(u.id) === String(option1Value),
                          );
                        } else if (value2 === 1 && option1Value) {
                          const index = units.findIndex(
                            (u) => String(u.id) === String(option1Value),
                          );
                          if (index > 0) allowedOption2.push(units[index - 1]);
                          if (index < units.length - 1)
                            allowedOption2.push(units[index + 1]);
                        } else if (value2 === 2) {
                          if (!option1Value) {
                            allowedOption2 = units.length
                              ? [units[0], units[units.length - 1]]
                              : [];
                          } else {
                            const index = units.findIndex(
                              (u) => String(u.id) === String(option1Value),
                            );
                            if (index === 0 && units.length) {
                              allowedOption2 = [units[units.length - 1]];
                            } else if (
                              index === units.length - 1 &&
                              units.length
                            ) {
                              allowedOption2 = [units[0]];
                            } else if (units.length) {
                              // If a middle element was selected, still restrict to first and last
                              allowedOption2 = [
                                units[0],
                                units[units.length - 1],
                              ];
                            } else {
                              allowedOption2 = [];
                            }
                          }
                        } else {
                          allowedOption2 = units;
                        }

                        return (
                          <Form.Item
                            name={['product', 'zonal', 'option2']}
                            rules={[
                              {
                                required: true,
                                message: t(
                                  'entity.offers.restrictions.restriction.ZONAL.axisRequired',
                                ),
                              },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (
                                    !value ||
                                    String(value) !== String(option1Value) ||
                                    value2 === ZERO_TRANSITIONS_NO
                                  ) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    new Error(
                                      t(
                                        'entity.offers.restrictions.restriction.ZONAL.sameUatNotAllowed',
                                      ),
                                    ),
                                  );
                                },
                              }),
                            ]}
                          >
                            <Select
                              placeholder={t(
                                'entity.offers.restrictions.restriction.ZONAL.uatStop',
                              )}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              {allowedOption2.map((unit) => (
                                <Select.Option key={unit.id} value={unit.id}>
                                  {unit.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Descriptions.Item>
                )}
              </>
            )
          ) : (
            <Descriptions.Item>
              {t('entity.offers.restrictions.restriction.ZONAL.nTransitions')}
            </Descriptions.Item>
          ))}
      </Descriptions>
    </>
  );
};

export default ZonalRestriction;
